/** Shared AI Analytics helpers — data is generated per selected site (multi-company). */

import { SiteDef, getSite } from '../profiles/dashboard/catalog'

export type SiteOption = { value: string; label: string; type: string }

export const COMPANY = {
  id: 2,
  name: 'Newcastle United',
}

/** @deprecated use catalog sites via AiFilterContext */
export const SITES: SiteOption[] = []

export const MODEL_STATUS = {
  version: 'v2.2026.10.01',
  championSince: '2026-10-01',
  lastRetrain: '2026-10-01 03:12',
  nextScheduledRetrain: '2026-10-08 03:00',
  status: 'healthy' as const,
  horizonDays: 30,
  originDate: '2026-09-27',
  wape: { h1: 15.5, h7: 16.4, h30: 18.3 },
  naiveWape: { h1: 24.4, h7: 24.8, h30: 31.4 },
  rowsTrained: 173160,
  meters: 8,
  featureTop: [
    'naive_seasonal_rel',
    'last_match_total_rel',
    'dayofyear',
    'log_level',
    'days_to_match',
    'is_match_day',
  ],
}

export type SiteWape = {
  site: string
  type: string
  wapeH1: number
  wapeH7: number
  wapeH30: number
  note?: string
}

export const SITE_WAPE: SiteWape[] = [
  { site: "St James' Park", type: 'stadium', wapeH1: 15.6, wapeH7: 16.4, wapeH30: 17.4 },
  { site: 'Academy', type: 'academy', wapeH1: 8.8, wapeH7: 8.9, wapeH30: 12.0 },
  {
    site: 'Training Ground',
    type: 'training_ground',
    wapeH1: 21.7,
    wapeH7: 23.6,
    wapeH30: 31.2,
    note: 'Unexplained winter peaks',
  },
  { site: 'Fan Zone', type: 'fan_zone', wapeH1: 9.3, wapeH7: 9.0, wapeH30: 10.3 },
  {
    site: 'Metro Centre',
    type: 'retail_store',
    wapeH1: 13.6,
    wapeH7: 13.5,
    wapeH30: 17.9,
    note: 'Short history — naive blend candidate',
  },
  {
    site: 'Media Centre',
    type: 'office_media',
    wapeH1: 18.5,
    wapeH7: 18.9,
    wapeH30: 18.1,
    note: 'Short history — naive blend candidate',
  },
]

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`
}

function addDays(iso: string, days: number) {
  const d = new Date(iso + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function buildForecastSeries(siteId: string, horizonDays = 30, site?: SiteDef) {
  const def = site || getSite(siteId)
  const origin = MODEL_STATUS.originDate
  const base = def?.baseKwh ?? 1000
  const mult = 1 + (def?.eventUplift ?? 0.1)
  const days = Math.max(1, Math.min(30, horizonDays))
  const weekendFactor = def?.weekendFactor ?? 1

  const categories: string[] = []
  const fullDates: string[] = []
  const predicted: number[] = []
  const actual: (number | null)[] = []
  const isMatch: boolean[] = []

  for (let h = 1; h <= days; h++) {
    const date = addDays(origin, h)
    fullDates.push(date)
    categories.push(date.slice(5))
    const d = new Date(date + 'T12:00:00')
    const dow = d.getDay()
    const isWeekend = dow === 0 || dow === 6
    const event =
      def?.type === 'stadium' || def?.type === 'leisure' || def?.type === 'fan_zone'
        ? dow === 6 || (dow === 0 && h % 3 === 0)
        : def?.type === 'retail_store' || def?.type === 'hotel'
        ? dow === 5 || dow === 6
        : h % 11 === 0
    isMatch.push(event)
    const season = 1 + (def?.seasonalityAmp ?? 0.2) * Math.sin((h / 30) * Math.PI)
    const noise = 1 + Math.sin(h * 0.7) * 0.04
    const pred = Math.round(
      base * season * (isWeekend ? weekendFactor : 1) * (event ? mult : 1) * noise
    )
    predicted.push(pred)
    if (h <= 5) {
      actual.push(Math.round(pred * (0.92 + (h % 3) * 0.04)))
    } else {
      actual.push(null)
    }
  }

  return { categories, fullDates, predicted, actual, isMatch, origin, horizonDays: days }
}

export function getSiteWape(siteLabel: string, site?: SiteDef) {
  const found = SITE_WAPE.find((r) => r.site === siteLabel || r.site.replace("'", '') === siteLabel)
  if (found) return found
  if (!site) return undefined
  const base = 12 + Math.abs(site.eventUplift) * 8
  return {
    site: site.name,
    type: site.type,
    wapeH1: Math.round(base * 10) / 10,
    wapeH7: Math.round((base + 1.2) * 10) / 10,
    wapeH30: Math.round((base + 2.5) * 10) / 10,
  }
}

export type AccuracyPoint = {
  date: string
  predicted: number
  actual: number
  ape: number
  isMatchDay: boolean
  anomaly?: boolean
}

export const ACCURACY_HISTORY: AccuracyPoint[] = (() => {
  const rows: AccuracyPoint[] = []
  const start = '2026-09-01'
  for (let i = 0; i < 30; i++) {
    const date = addDays(start, i)
    const isMatch = i % 7 === 2
    const predicted = Math.round(17500 * (isMatch ? 1.7 : 1) * (1 + Math.sin(i) * 0.03))
    const error = isMatch ? 0.12 + (i % 5) * 0.01 : 0.08 + (i % 4) * 0.015
    const sign = i % 2 === 0 ? 1 : -1
    const actual = Math.round(predicted * (1 + sign * error * 0.5))
    const ape = (Math.abs(predicted - actual) / actual) * 100
    rows.push({
      date,
      predicted,
      actual,
      ape: Math.round(ape * 10) / 10,
      isMatchDay: isMatch,
      anomaly: i === 11 || i === 24,
    })
  }
  return rows
})()

export const ACCURACY_KPIS = {
  rolling7Wape: 14.8,
  rolling30Wape: 16.2,
  matchDayWape: 15.1,
  nonMatchWape: 16.9,
  unexplainedAnomalies: 2,
  driftStatus: 'stable' as const,
}

export function buildAccuracyHistoryForSite(siteId: string, site?: SiteDef) {
  const def = site || getSite(siteId)
  const scale = (def?.baseKwh ?? 17500) / 17500
  return ACCURACY_HISTORY.map((r) => ({
    ...r,
    predicted: Math.round(r.predicted * scale),
    actual: Math.round(r.actual * scale),
    isMatchDay: r.isMatchDay,
  }))
}

export type EventCandidate = {
  id: string
  date: string
  site: string
  suggestedType: string
  name: string
  source: string
  confidence: 'high' | 'medium' | 'low'
  status: 'pending' | 'approved' | 'rejected' | 'auto_applied'
  details: string
  kickoff?: string
  attendance?: number
}

export const EVENT_CANDIDATES: EventCandidate[] = [
  {
    id: 'c1',
    date: '2026-10-17',
    site: "St James' Park",
    suggestedType: 'Match',
    name: 'NUFC vs Aston Villa',
    source: 'openfootball + Wikipedia',
    confidence: 'high',
    status: 'pending',
    details: 'Premier League home fixture',
    kickoff: '15:00',
    attendance: 52200,
  },
  {
    id: 'c2',
    date: '2026-10-25',
    site: "St James' Park",
    suggestedType: 'Match',
    name: 'NUFC vs Chelsea',
    source: 'openfootball',
    confidence: 'high',
    status: 'pending',
    details: 'Premier League home fixture',
    kickoff: '17:30',
  },
  {
    id: 'c3',
    date: '2026-11-08',
    site: "St James' Park",
    suggestedType: 'Concert',
    name: 'Stadium concert (TBC)',
    source: 'curated feed',
    confidence: 'medium',
    status: 'pending',
    details: 'Non-football event — review recommended',
  },
  {
    id: 'c4',
    date: '2026-10-03',
    site: "St James' Park",
    suggestedType: 'Match',
    name: 'NUFC vs Brighton',
    source: 'openfootball',
    confidence: 'high',
    status: 'auto_applied',
    details: 'Auto-applied (high confidence match)',
    kickoff: '15:00',
  },
  {
    id: 'c5',
    date: '2026-09-20',
    site: "St James' Park",
    suggestedType: 'Rugby',
    name: 'Club rugby event',
    source: 'curated feed',
    confidence: 'low',
    status: 'rejected',
    details: 'Duplicate of existing event',
  },
]

export const RETRAIN_HISTORY = [
  { date: '2026-10-01', wapeH7: 16.4, status: 'promoted', note: 'Champion' },
  { date: '2026-09-24', wapeH7: 16.9, status: 'promoted', note: 'Previous champion' },
  { date: '2026-09-17', wapeH7: 18.1, status: 'rejected', note: 'Worse than champion — kept old' },
  { date: '2026-09-10', wapeH7: 17.2, status: 'promoted', note: '' },
]
